import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { productKeyAtom } from "src/atoms";
import {
  isTest,
  ProductKey,
  WRAPSODY_CLOUD_URL,
  WRAPSODY_ECO_CLOUD_URL,
} from "src/config/config";
import { isEmbeddedSession } from "src/utils/embedded";
export const useRouter = () => {
  const navigate = useNavigate();
  // iframe(OCI onceLogin) 임베드 세션에서는 내부 이동을 replace로 처리해
  // 부모(eCo) 히스토리에 항목이 쌓이지 않게 한다. → 뒤로가기 시 제품으로 복귀
  const isEmbedded = isEmbeddedSession();
  const router: typeof navigate = ((to: any, options?: any) => {
    if (typeof to === "number") return navigate(to);
    return navigate(to, {
      ...options,
      replace: isEmbedded || Boolean(options?.replace),
    });
  }) as typeof navigate;
  const params = useParams();
  const pathname = window.location.pathname.substring(3);
  const [productKey, setProductKey] = useRecoilState(productKeyAtom);
  const search = window.location.search;
  const { i18n } = useTranslation();
  const lang = i18n.language;

  return {
    router: router,
    currentPath: window.location.pathname,
    reload: () => window.location.reload(),
    lang,
    productReload: ({
      productKey,
      urlPrefix,
    }: {
      productKey: ProductKey;
      urlPrefix?: string;
    }) => {
      if (productKey === "WRAPSODY") {
        return (window.parent.location.href = `${WRAPSODY_CLOUD_URL}/filesync/ui/reload`);
      } else if (productKey === "WRAPSODY_ECO") {
        return (window.parent.location.href = `${WRAPSODY_ECO_CLOUD_URL}/${urlPrefix}/home`);
      }
    },
    params: params,
    parentRouteTo: ({
      path,
      isReload = false,
    }: {
      path: string;
      isReload?: boolean;
    }) => {
      if (isReload) {
        window.parent.location.reload();
      } else window.parent.location.href = path;
    },
    routeTo: (path: string) => router(path),
    changeLang: () => {
      const changeLang = lang === "ko" ? "en" : "ko";
      i18n.changeLanguage(changeLang);
      router(`/${changeLang}${pathname}${search}`);
      goToTop();
    },
    goHome: () => {
      router(`/${lang}`);
      // 제품 선택 초기화(런타임 undefined) — 아톰 타입 정합용 캐스팅
      setProductKey(undefined as unknown as ProductKey);
    },
    goLogin: () => {
      router(`/${lang}/login`);
      goToTop();
    },
    goJoin: () => {
      router(`/${lang}/join`);
      goToTop();
    },
    goConsole: () => {
      router(`/${lang}/console`);
      goToTop();
    },
    goServiceList: () => {
      router(`/${lang}/service/list`);
      goToTop();
    },
    goBill: () => {
      router(`/${lang}/bill`);
      goToTop();
    },
    goMyAccount: () => router(`/${lang}/my-account`),
    goTest: () => router(`/${lang}/test`),

    // site
    goSiteType: () => {
      router(`/${lang}/site/type`);
      goToTop();
    },
    goSiteTrial: () => {
      router(`/${lang}/site/trial`);
      goToTop();
    },
    goSitePromotion: ({
      renewCode,
      type,
    }: {
      renewCode?: string;
      type?: string;
    }) => {
      if (type) {
        if (renewCode) {
          router(`/${lang}/site/promotion?type=${type}&code=${renewCode}`);
        } else {
          router(`/${lang}/site/promotion?type=${type}`);
        }
      } else if (renewCode) {
        router(`/${lang}/site/promotion?type=renew&code=${renewCode}`);
      } else router(`/${lang}/site/promotion`);
      goToTop();
    },
    goPay: ({ pay = "month", type }: { pay?: string; type: string }) => {
      // console.log("goPay", pay, type);
      router(`/${lang}/site/pay?pay=${pay}&type=${type}`);
      goToTop();
    },
    goPayBasic: () => {
      router(`/${lang}/site/pay?pay=month&type=basic`);
      goToTop();
    },
    goPayPlus: () => {
      router(`/${lang}/site/pay?pay=month&type=plus`);
      goToTop();
    },
    // Footer 아이콘
    goToBlog: () => window.open(`https://blog.naver.com/fasooai`),
    goToYoutube: () => {
      if (lang === "ko") {
        window.open(`https://www.youtube.com/@FasooAI`);
      } else if (lang === "en") {
        window.open(`https://www.youtube.com/@FasooAIGlobal`);
      }
    },
    goToFacebook: () => {
      if (lang === "ko") {
        window.open(`https://www.facebook.com/fasooai.korea/`);
      } else if (lang === "en") {
        window.open(`https://www.facebook.com/fasooai.global/`);
      }
    },
    goToInsta: () => {
      if (lang === "ko") {
        window.open(`https://www.instagram.com/fasoo_ai_official/`);
      } else if (lang === "en") {
        window.open(`https://www.instagram.com/fasoo_ai_global/`);
      }
    },
    openWrapsody: () => {
      window.open(WRAPSODY_CLOUD_URL);
    },
    openEco: ({ domainName }: { domainName: string }) => {
      window.open(`https://${domainName}`);
    },
    openMindSat: ({ domainName }: { domainName: string }) => {
      if (isTest) {
        window.open(`http://146.56.156.112/secure-mind-sat-admin`);
      } else {
        window.open(`https://${domainName}`);
      }
    },
    goTerms: () => {
      router(`/${lang}/terms`);
      goToTop();
    },
    goTermsComp: () => {
      router(`/${lang}/termsComp`);
      goToTop();
    },
    goInquiry: ({ product }: { product: "wrapsody" | "eco" }) => {
      router(`/${lang}/inquiry/dspm`);
      goToTop();
    },
  };
  function goToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};
